import React, { FC, useContext } from 'react'
import { t, Trans } from '@lingui/macro'
import { IconCircleNotStarted } from '../../../components/common/icon/IconCircleNotStarted'
import { IconCircleProgress } from '../../../components/common/icon/IconCircleProgress'
import Loader from '../../../components/common/loader/Loader'
import { AnalyticsHeader } from '../AnalyticsHeader'
import BorderedBox from '../../../components/common/bordered-box/BorderedBox'
import { AnalyticsHighlight } from '../AnalyticsHighlight'
import PathwaysOverviewTable from './PathwaysOverviewTable'
import {
  usePathwaysRelatedCampaignsQuery,
  usePathwaysWithEnrolmentStatusCountsQuery
} from './Pathways.codegen'
import { AnalyticsContext } from '../AnalyticsContext'
import { AnalyticsFiltersEnum } from '../constants'
import { SearchableEntity } from '../../../components/common/selectEntitiesForm'

interface IProps {
  showFilterPanel?: boolean
}

const Pathways: FC<IProps> = ({ showFilterPanel }: IProps) => {
  const { filterAsQueryVariables } = useContext(AnalyticsContext)
  const { data, loading } = usePathwaysWithEnrolmentStatusCountsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      filter: filterAsQueryVariables
    }
  })

  const { refetch: loadRelatedCampaignsQuery } =
    usePathwaysRelatedCampaignsQuery({
      fetchPolicy: 'no-cache',
      variables: {
        filter: filterAsQueryVariables
      },
      skip: true
    })
  const [relatedCampaigns, setRelatedCampaigns] = React.useState<
    null | SearchableEntity[]
  >(null)

  const loadRelatedCampaigns = React.useCallback(async () => {
    const response = await loadRelatedCampaignsQuery()
    const campaigns: SearchableEntity[] =
      response.data?.analytics.pathways.relatedCampaigns ?? []
    setRelatedCampaigns(campaigns)
    return campaigns
  }, [])

  if (loading || !data || !data?.analytics || !data?.analytics.pathways) {
    return <Loader />
  }

  const {
    completedCount,
    failedCount,
    inProgressCount,
    notStartedCount,
    overdueCount
  } = data.analytics.pathways

  const renderAnalyticsBoxes = () => {
    return (
      <div className="AnalyticsRow">
        <BorderedBox className="AnalyticsRow__box">
          <div className="AnalyticsHighlight__row">
            <AnalyticsHighlight
              value={completedCount}
              icon="check"
              description={t`Completed`}
              iconColor="green"
            />
            {!!failedCount && (
              <AnalyticsHighlight
                value={failedCount}
                icon="ban"
                description={t`Failed`}
                iconColor="red"
              />
            )}
            <AnalyticsHighlight
              value={inProgressCount}
              icon={<IconCircleProgress boxSize="20px" />}
              description={t`In progress`}
              iconColor="blue"
            />
            <AnalyticsHighlight
              value={notStartedCount}
              icon={<IconCircleNotStarted boxSize="20px" />}
              description={t`Not started`}
              iconColor="grey"
            />
            <AnalyticsHighlight
              value={overdueCount}
              icon="clock"
              description={t`Overdue`}
              iconColor="red"
            />
          </div>
        </BorderedBox>
      </div>
    )
  }

  return (
    <div className="PathwaysAnalyticsPage">
      <AnalyticsHeader
        showFilterPanel={showFilterPanel}
        activeFilters={[
          AnalyticsFiltersEnum.INCLUDE_ADMINS,
          AnalyticsFiltersEnum.INCLUDE_ARCHIVED,
          AnalyticsFiltersEnum.DATE_FILTER,
          AnalyticsFiltersEnum.PATHWAY_FILTER,
          AnalyticsFiltersEnum.GROUP_FILTER,
          AnalyticsFiltersEnum.CAMPAIGN_FILTER
        ]}
        customLoadedEntities={{
          [AnalyticsFiltersEnum.CAMPAIGN_FILTER]: relatedCampaigns
        }}
        customDataLoaders={{
          [AnalyticsFiltersEnum.CAMPAIGN_FILTER]: loadRelatedCampaigns
        }}
      >
        <Trans>Pathways</Trans>
      </AnalyticsHeader>

      <>
        {renderAnalyticsBoxes()}
        <PathwaysOverviewTable />
      </>
    </div>
  )
}

export default Pathways
